/**
 * Подписи звонка: чей звонок, в какой группе и как назвать участника без имени.
 *
 * <p>Вынесено из экранов, чтобы правила проверялись тестом (E2E-8): входящий групповой
 * показывал имя звонящего вместо группы, а участник без имени в комнате был «Contact».
 */
import { normCallUserId } from './callSignalRules';

/** Что известно об участнике из списка участников группы. */
export type CallMemberInfo = {
  displayName?: string | null;
  publicHandle?: string | null;
};

/** Справочник участников группы по нормализованному id (см. {@link normCallUserId}). */
export type CallMemberDirectory = Readonly<Record<string, CallMemberInfo>>;

export function callMemberDirectory(
  members: readonly ({ userId: string } & CallMemberInfo)[],
): CallMemberDirectory {
  const out: Record<string, CallMemberInfo> = {};
  for (const m of members) {
    const key = normCallUserId(m.userId);
    if (key) out[key] = { displayName: m.displayName, publicHandle: m.publicHandle };
  }
  return out;
}

function nonBlank(v: string | null | undefined): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length > 0 ? s : null;
}

/** identity LiveKit у настоящих пользователей — их UUID; показывать его человеку незачем. */
function looksLikeUserId(identity: string): boolean {
  return /^[0-9a-f]{32}$/.test(normCallUserId(identity));
}

/**
 * Имя участника звонка.
 *
 * <p>Порядок: имя из списка участников группы → имя из токена LiveKit → имя из сигналов →
 * ник → identity, если это не UUID (боты нагрузочного теста) → «Участник N».
 */
export function resolveCallParticipantName(input: {
  identity: string;
  liveKitName?: string | null;
  signalName?: string | null;
  directory?: CallMemberDirectory | null;
  /** Номер участника в комнате, с единицы — для «Участник N». */
  ordinal: number;
  participantLabel: (n: number) => string;
}): string {
  const member = input.directory?.[normCallUserId(input.identity)];
  const handle = nonBlank(member?.publicHandle);
  return (
    nonBlank(member?.displayName) ??
    nonBlank(input.liveKitName) ??
    nonBlank(input.signalName) ??
    (handle ? `#${handle}` : null) ??
    (nonBlank(input.identity) && !looksLikeUserId(input.identity) ? input.identity.trim() : null) ??
    input.participantLabel(input.ordinal)
  );
}

/**
 * Название группы из data-пуша о звонке. Сервер кладёт в `title` название чата
 * (`PushDeliveryService.titleForChat`), и для группы это её имя.
 */
export function groupTitleFromPushData(data: Record<string, unknown>): { groupTitle?: string } {
  if (data.callScope !== 'group') return {};
  const title = nonBlank(typeof data.title === 'string' ? data.title : null);
  return title ? { groupTitle: title } : {};
}

/**
 * Заголовок входящего: в группе — её название, а звонящий уходит в подзаголовок
 * («Иван звонит»). Без названия группы — как раньше, имя звонящего.
 */
export function incomingCallHeadline(p: {
  callerName: string;
  callScope?: 'direct' | 'group';
  groupTitle?: string;
}): { title: string; callerName: string | null } {
  const group = p.callScope === 'group' ? nonBlank(p.groupTitle) : null;
  if (group) {
    return { title: group, callerName: nonBlank(p.callerName) };
  }
  return { title: p.callerName, callerName: null };
}
